import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { AuthorizedContact } from "../../../../../resources-vue/vue-models/authorized-contact";
import { ToastrService } from "../../../../../services/toastr.service";
import { IContactsOrder, IContactsOrderQueryParam } from "../../../../../resources-vue/vue-interfaces/i-get-patient";
import { ContactRoles, Relationships, HttpStatusCodeEnum } from "../../../../../enums/enums";
import { useConvertToPendingPal } from "../../store/useConvertToPendingPal";
import { PatientService } from "../../../../../services/patient.service";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import AuthorizedContactsForm from "./AuthorizedContactsForm/AuthorizedContactsForm.vue";
import AuthorizedContactsList from "./AuthorizedContactsList/AuthorizedContactsList.vue";

export default defineComponent({
    components: {
        FormSection,
        AuthorizedContactsForm,
        AuthorizedContactsList
    },

    data(vm) {
        return {
            _toastrService: null,
            _patientService: null,
            totalContacts: 0,
            contactLimit: 10,
            showContactForm: false,
            isReOrderRequested: false,
            isLoading: false,
            isSavingReOrder: false,
            cachedContact: [],
            relationshipsEnum: Array.from(Relationships.enumWithDescriptions.values()),
            contactRolesEnum: Array.from(ContactRoles.enumWithDescriptions.values()),
            contact: new AuthorizedContact()
        };
    },

    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._patientService = Container.instance.get(PatientService);
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["patientDetails", "accountId", "application", "patientId"])
    },

    methods: {
        openAddContact() {
            this.showContactForm = true;
            this.contact.contactOrder = this.totalContacts;
            this.contact.application = this.application;
        },
        closeAddContact() {
            this.isLoading = false;
            this.showContactForm = false;
            this.contact = new AuthorizedContact();
        },
        async addContact() {
            const validated = await (this.$refs.addContact as any).v$.$validate();
            if (this.isEmptyContact(this.contact)) {
                this._toastrService.info({
                    title: `Empty Contact`,
                    message: `Please add the necessary information to save the contact.`
                });
                return;
            }
            if (!validated) {
                return;
            }
            try {
                this.isLoading = true;
                this.contact = this.addressAndPhoneNumberCheck(this.contact);
                if (this.totalContacts === 0) {
                    this.contact.isPrimary = true;
                }
                this.contact.accountId = this.accountId;
                this.contact.application = this.application;
                this.contact.patientId = this.patientId;
                const response = await this._patientService.addPalAuthorizedContact(this.patientId, {
                    palliativeRequest: this.contact,
                    accountId: this.accountId,
                    patientId: this.patientId,
                    application: this.application
                });
                if (response.Data.id) {
                    this._toastrService.success({
                        title: "Success",
                        message: "Contact has been successfully added!"
                    });
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: "There was a problem while adding a contact. Please try again."
                    });
                }
                this.closeAddContact();
                (this.$refs.contactList as any)?.getContacts?.();
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: `Error`,
                    message: "There was a problem while adding a contact. Please try again."
                });
            } finally {
                this.isLoading = false;
            }
        },
        requestReOrder() {
            this.cachedContact =
                (this.$refs.contactList as any)?.contacts.length > 0 ? [(this.$refs.contactList as any)?.contacts] : [];
            this.isReOrderRequested = true;
        },
        async confirmReOrder() {
            try {
                this.isSavingReOrder = true;
                let contactsOrder: IContactsOrder[] = (this.$refs.contactList as any)?.contacts.map(
                    (contact: AuthorizedContact) => {
                        return {
                            contactId: contact.id,
                            contactOrder: contact.contactOrder
                        };
                    }
                );
                await this.reorderContacts(contactsOrder);
                (this.$refs.contactList as any)?.getContacts();
            } catch (e) {
                if (e.status === HttpStatusCodeEnum.NotModified) {
                    this._toastrService.info({
                        title: "No Changes Detected",
                        message: "There were no changes made to the contacts order."
                    });
                } else {
                    console.error(e);
                    this._toastrService.error({
                        title: `Error`,
                        message: "There was a problem while re-ordering the contacts. Please try again."
                    });
                }
            } finally {
                this.isReOrderRequested = false;
                this.isSavingReOrder = false;
            }
        },
        deleteAuthorizedContact(id: string) {
            return this._patientService.deleteAuthorizedContact({
                contactId: id,
                application: this.application,
                accountId: this.accountId
            });
        },
        async updateAuthorizedContact(contact: AuthorizedContact) {
            contact.application = this.application;
            contact.patientId = this.patientId;

            let payload = this.patientDetails;
            payload.authorizedContacts = contact;

            const response = await this._patientService.updateConvertPalliativeReferral({
                payload: payload,
                accountId: this.accountId,
                patientId: this.patientId,
                application: this.application,
                contactId: contact.id,
                tabName: "authorizedContacts"
            });
        },
        cancelReOrder() {
            if (this.$refs.contactList) {
                (this.$refs.contactList as any).contacts = this.cachedContact;
            }
            this.isReOrderRequested = false;
        },
        isEmptyContact(contact: AuthorizedContact) {
            let isContactEmpty = false;
            isContactEmpty = Object.entries(contact).every((contactProperty) => {
                let isEmpty = true;
                let checkNotRequired = ["id", "isPrimary", "contactOrder", "address"];
                if (!checkNotRequired.includes(contactProperty[0])) {
                    if (Array.isArray(contactProperty[1])) {
                        isEmpty = contactProperty[1].length === 0;
                    } else {
                        isEmpty = !contactProperty[1];
                    }
                } else if (contactProperty[0] === "address" && !contactProperty[1].addressLine1) {
                    isEmpty = true;
                }
                return isEmpty;
            });
            return isContactEmpty;
        },

        addressAndPhoneNumberCheck(contact: AuthorizedContact) {
            if (!contact?.address?.addressLine1) {
                contact.address = null;
            }
            if (!contact?.phoneNumbers[0]?.number && !contact?.phoneNumbers[1]?.number) {
                contact.phoneNumbers = [];
            }
            return contact;
        },
        reorderContacts(contactOrder: IContactsOrder[]) {
            const contactsOrderQuery: IContactsOrderQueryParam = {
                contactsOrder: contactOrder,
                application: this.application,
                accountId: this.accountId
            };
            return this._patientService.reorderAuthorizedContacts(contactsOrderQuery);
        },
        async getContacts(patientId: string) {
            const payload = {
                application: this.application,
                accountId: this.accountId,
                patientId: patientId
            };
            let contacts = await this._patientService.getPalAuthorizedContact(patientId, payload);
            this.totalContacts = contacts?.Data.length;
            return contacts;
        }
    }
});
